import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { NotificationToast } from "@/components/NotificationBell";

// Routes hit on the first page load (login → dashboard → kiosk flow) stay eagerly
// imported so there's no flash. Everything else is lazy — biggest wins are the
// admin tree and accountant pages, which a regular employee never needs.
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { Welcome } from "@/pages/Welcome";
import { Onboarding } from "@/pages/Onboarding";
import { Dashboard } from "@/pages/Dashboard";
import { TimeTracking } from "@/pages/TimeTracking";
import { Profile } from "@/pages/Profile";
import { SOPs } from "@/pages/SOPs";
import { Resources } from "@/pages/Resources";
import { Chat } from "@/pages/Chat";
import { Kiosk } from "@/pages/Kiosk";
import { Announcements } from "@/pages/Announcements";
import { Directory } from "@/pages/Directory";
import { TimeOff } from "@/pages/TimeOff";
import { TasksHome } from "@/pages/Tasks";
import { BoardView } from "@/pages/Board";
import { PtoAdmin } from "@/pages/admin/PtoAdmin";
import { Orders, OrdersAdmin } from "@/pages/Orders";
import { Support } from "@/pages/Support";
import { Kudos, Suggestions, SuggestionsAdmin } from "@/pages/Culture";
import { DevRequests } from "@/pages/DevRequests";
import { DevRequestsAdmin } from "@/pages/admin/DevRequestsAdmin";
import { Checklists, ChecklistsAdmin } from "@/pages/Checklists";
import { Training, TrainingAdmin, OneOnOnes, Goals } from "@/pages/Workforce";
import { DisciplineAcknowledge } from "@/pages/DisciplineAcknowledge";
import { Reviews, ReviewsAdmin } from "@/pages/Reviews";
import { NewHires } from "@/pages/NewHires";
import { OffboardingAdmin, DisciplineAdmin, SafetyAdmin, BackgroundChecksAdmin } from "@/pages/HRAdmin";

// Admin pages — lazy. Each becomes its own chunk Vite can defer until the route
// is hit. Regular employees never download any of this.
const CrmLayout = lazy(() => import("@/pages/crm/CrmLayout").then(m => ({ default: m.CrmLayout })));
const CrmContacts = lazy(() => import("@/pages/crm/Contacts").then(m => ({ default: m.CrmContacts })));
const CrmCalendar = lazy(() => import("@/pages/crm/Calendar").then(m => ({ default: m.CrmCalendar })));
const CrmPipeline = lazy(() => import("@/pages/crm/Pipeline").then(m => ({ default: m.CrmPipeline })));
const AdminEmployees = lazy(() => import("@/pages/admin/Employees").then(m => ({ default: m.Employees })));
const Infrastructure = lazy(() => import("@/pages/admin/infra/Infrastructure").then(m => ({ default: m.Infrastructure })));
const Integrations = lazy(() => import("@/pages/admin/infra/Integrations").then(m => ({ default: m.Integrations })));
const Provisioning = lazy(() => import("@/pages/admin/Provisioning").then(m => ({ default: m.Provisioning })));
const TimePayroll = lazy(() => import("@/pages/admin/TimePayroll").then(m => ({ default: m.TimePayroll })));
const SOPsAdmin = lazy(() => import("@/pages/admin/SOPsAdmin").then(m => ({ default: m.SOPsAdmin })));
const ResourcesAdmin = lazy(() => import("@/pages/admin/ResourcesAdmin").then(m => ({ default: m.ResourcesAdmin })));
const ChatAdmin = lazy(() => import("@/pages/admin/ChatAdmin").then(m => ({ default: m.ChatAdmin })));
const AuditLog = lazy(() => import("@/pages/admin/AuditLog").then(m => ({ default: m.AuditLog })));
const DriveAdmin = lazy(() => import("@/pages/admin/DriveAdmin").then(m => ({ default: m.DriveAdmin })));
const Roadmap = lazy(() => import("@/pages/admin/Roadmap").then(m => ({ default: m.Roadmap })));
const RitualsHistory = lazy(() => import("@/pages/RitualsHistory").then(m => ({ default: m.RitualsHistory })));
const ProductDevHome = lazy(() => import("@/pages/admin/ProductDev").then(m => ({ default: m.ProductDevHome })));
const ProductDevDetail = lazy(() => import("@/pages/admin/ProductDev").then(m => ({ default: m.ProductDevDetail })));
const ProductDevCompare = lazy(() => import("@/pages/admin/ProductDev").then(m => ({ default: m.ProductDevCompare })));
const ProductDevVendorsHome = lazy(() => import("@/pages/admin/ProductDevVendors").then(m => ({ default: m.ProductDevVendorsHome })));
const ProductDevVendorDetail = lazy(() => import("@/pages/admin/ProductDevVendors").then(m => ({ default: m.ProductDevVendorDetail })));
const ProductDevRoadmap = lazy(() => import("@/pages/admin/ProductDevExtras").then(m => ({ default: m.ProductDevRoadmap })));
const ProductDevCatalog = lazy(() => import("@/pages/admin/ProductDevExtras").then(m => ({ default: m.ProductDevCatalog })));
const ProductDevPricingMatrix = lazy(() => import("@/pages/admin/ProductDevExtras").then(m => ({ default: m.ProductDevPricingMatrix })));
const ProductDevAnalytics = lazy(() => import("@/pages/admin/ProductDevExtras").then(m => ({ default: m.ProductDevAnalytics })));
const BugsAdmin = lazy(() => import("@/pages/admin/Bugs").then(m => ({ default: m.BugsAdmin })));
const AccessReview = lazy(() => import("@/pages/admin/AccessReview").then(m => ({ default: m.AccessReview })));
const ObjectivesAdmin = lazy(() => import("@/pages/admin/Objectives").then(m => ({ default: m.ObjectivesAdmin })));
const Mission = lazy(() => import("@/pages/Mission").then(m => ({ default: m.Mission })));
const DecisionsAdmin = lazy(() => import("@/pages/admin/Decisions").then(m => ({ default: m.DecisionsAdmin })));
const JobBoard = lazy(() => import("@/pages/admin/JobBoard").then(m => ({ default: m.JobBoard })));
const JobBoardAdmin = lazy(() => import("@/pages/admin/JobBoard").then(m => ({ default: m.JobBoardAdmin })));
const TrainingStatusAdmin = lazy(() => import("@/pages/admin/TrainingStatus").then(m => ({ default: m.TrainingStatusAdmin })));
const BoardAccess = lazy(() => import("@/pages/admin/BoardAccess").then(m => ({ default: m.BoardAccess })));
const Wins = lazy(() => import("@/pages/Wins").then(m => ({ default: m.Wins })));
const Huddle = lazy(() => import("@/pages/Huddle").then(m => ({ default: m.Huddle })));
const Handbook = lazy(() => import("@/pages/Handbook").then(m => ({ default: m.Handbook })));
const Polls = lazy(() => import("@/pages/Polls").then(m => ({ default: m.Polls })));



const AdminHome = lazy(() => import("@/pages/admin/AdminHome").then(m => ({ default: m.AdminHome })));
const OrgChart = lazy(() => import("@/pages/admin/OrgChart").then(m => ({ default: m.OrgChart })));
const StoreLocator = lazy(() => import("@/pages/admin/StoreLocator").then(m => ({ default: m.StoreLocator })));
const McpServer = lazy(() => import("@/pages/admin/infra/McpServer").then(m => ({ default: m.McpServer })));
const CustomerJourney = lazy(() => import("@/pages/d2c/CustomerJourney").then(m => ({ default: m.CustomerJourney })));
const AcctRoster = lazy(() => import("@/pages/accountant/AcctRoster").then(m => ({ default: m.AcctRoster })));
const AcctTime = lazy(() => import("@/pages/accountant/AcctTime").then(m => ({ default: m.AcctTime })));

function Deactivated() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-offwhite p-6 text-center">
      <div className="card max-w-md">
        <h1 className="mb-2 text-2xl font-bold">Account deactivated</h1>
        <p className="text-sm text-gray2">Your account has been set to inactive. Contact your admin to reactivate it.</p>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="p-10 text-center text-sm text-gray2">Page not found.</div>
  );
}

function PageLoading() {
  return <div className="flex h-screen items-center justify-center text-sm text-gray2">Loading…</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationToast />
      <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/deactivated" element={<Deactivated />} />
        {/* /apply is not a route: netlify.toml 302s it to the wholesale
            application on Shopify before the app loads. */}

        {/* Kiosk is a special protected route that uses its own layout (no sidebar). */}
        <Route path="/kiosk" element={<ProtectedRoute><Kiosk /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="/time" element={<TimeTracking />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/sops" element={<SOPs />} />
          {/* Resources was renamed Documents (HQ strip-down); old links still land. */}
          <Route path="/documents" element={<Resources />} />
          <Route path="/resources" element={<Navigate to="/documents" replace />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/announcements" element={<Announcements />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/pto" element={<TimeOff />} />
          <Route path="/tasks" element={<TasksHome />} />
          <Route path="/tasks/:boardId" element={<BoardView />} />
          <Route path="/orders" element={<Orders />} />
          {/* HQ-CRM-TAB-001: the CRM tab and its sub-tabs. */}
          <Route path="/crm" element={<CrmLayout />}>
            <Route index element={<Navigate to="/crm/contacts" replace />} />
            <Route path="/crm/contacts" element={<CrmContacts />} />
            <Route path="/crm/pipelines" element={<CrmPipeline />} />
            <Route path="/crm/calendar" element={<CrmCalendar />} />
          </Route>
          <Route path="/rituals" element={<RitualsHistory />} />
          <Route path="/support" element={<ProtectedRoute require="support"><Support /></ProtectedRoute>} />
          <Route path="/kudos" element={<Kudos />} />
          <Route path="/suggestions" element={<Suggestions />} />
          <Route path="/dev-requests" element={<DevRequests />} />
          <Route path="/checklists" element={<Checklists />} />
          <Route path="/training" element={<Training />} />
          <Route path="/one-on-ones" element={<OneOnOnes />} />
          {/* HQ-DISCIPLINE-ROUTE-001: the employee's own view of a write-up and
              the only place it can be acknowledged. Row-level security decides
              who sees which record, so no extra route gate is needed here. */}
          <Route path="/discipline/:id" element={<DisciplineAcknowledge />} />
          <Route path="/goals" element={<Goals />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/new-hires" element={<NewHires />} />
          <Route path="/handbook" element={<Handbook />} />
          <Route path="/polls" element={<Polls />} />
          <Route path="/mission" element={<Mission />} />
          <Route path="/wins" element={<Wins />} />
          <Route path="/jobs" element={<JobBoard />} />
          <Route path="/huddle" element={<Huddle />} />

          {/* The Priority Engine screens were removed (HQ strip-down); its
              tables stay. Old links, notifications and a board card's
              priority link land on the boards instead of a blank page. */}
          <Route path="/priority" element={<Navigate to="/tasks" replace />} />
          <Route path="/priority/*" element={<Navigate to="/tasks" replace />} />

          {/* Every /admin/* route gates on its own permission key. Admins/super_admins
              get all of them via role defaults; any other employee can be granted access
              to a specific page via extra_permissions in admin → Employees → Permissions. */}
          {/* ── D2C ──────────────────────────────────────────────────────
              One read-only window on every automated message a customer can
              receive, across the eight systems that send them. The journey
              switcher inside the page handles D2C vs Wholesale, so a second
              storefront is a line in config/journeys.json rather than a route. */}
          <Route path="/d2c/customer-journey"
            element={<ProtectedRoute permission="d2c.journey.view"><CustomerJourney /></ProtectedRoute>} />

          <Route path="/admin">
            {/* HQ-ADMIN-LANDING-001. This parent had 79 children and no index,
                so /admin itself rendered nothing at all — no heading, no links,
                not even an error. */}
            <Route index element={<ProtectedRoute><AdminHome /></ProtectedRoute>} />
            {/* Super Admin → Infrastructure. Gated on its own permission, which
                hq.ai.manage does not imply. */}
            <Route path="infrastructure"     element={<ProtectedRoute permission="hq.infrastructure.view"><Infrastructure /></ProtectedRoute>} />
            <Route path="integrations"       element={<ProtectedRoute permission="hq.infrastructure.view"><Integrations /></ProtectedRoute>} />
            {/* HQ's own MCP server: what claude.ai may read, and who is connected. */}
            {/* The public "Stores near you" map's only editor. Moved here from
                /crm/stores when the Sales CRM was removed (HQ strip-down). */}
            <Route path="store-locator" element={<ProtectedRoute permission="hq.admin.store_locator"><StoreLocator /></ProtectedRoute>} />
            <Route path="mcp" element={<ProtectedRoute permission="hq.mcp.manage"><McpServer /></ProtectedRoute>} />
            <Route path="employees"          element={<ProtectedRoute permission="hq.employees.view"><AdminEmployees /></ProtectedRoute>} />
            {/* Who reports to whom. hq_can_manage_employee_record falls back to it,
                so with the column empty a manager can manage nobody. */}
            <Route path="org" element={<ProtectedRoute permission="hq.employees.edit"><OrgChart /></ProtectedRoute>} />
            <Route path="provisioning"       element={<ProtectedRoute permission="hq.employees.invite"><Provisioning /></ProtectedRoute>} />
            <Route path="access"             element={<ProtectedRoute permission="hq.access_review.view"><AccessReview /></ProtectedRoute>} />
            <Route path="board-access"       element={<ProtectedRoute permission="hq.boards.manage_access"><BoardAccess /></ProtectedRoute>} />
            <Route path="objectives"         element={<ProtectedRoute permission="hq.mission.edit"><ObjectivesAdmin /></ProtectedRoute>} />
            <Route path="decisions"          element={<ProtectedRoute permission="hq.admin.decisions"><DecisionsAdmin /></ProtectedRoute>} />
            <Route path="jobs"               element={<ProtectedRoute permission="hq.admin.jobs"><JobBoardAdmin /></ProtectedRoute>} />
            <Route path="time-payroll"       element={<ProtectedRoute permission="hq.admin.time_payroll"><TimePayroll /></ProtectedRoute>} />
            <Route path="pto"                element={<ProtectedRoute permission="hq.admin.pto"><PtoAdmin /></ProtectedRoute>} />
            <Route path="reviews"            element={<ProtectedRoute permission="hq.reviews.write"><ReviewsAdmin /></ProtectedRoute>} />
            <Route path="orders"             element={<ProtectedRoute permission="hq.admin.orders"><OrdersAdmin /></ProtectedRoute>} />
            <Route path="training"           element={<ProtectedRoute permission="hq.admin.training"><TrainingAdmin /></ProtectedRoute>} />
            <Route path="training-status"    element={<ProtectedRoute permission="hq.training.assign"><TrainingStatusAdmin /></ProtectedRoute>} />
            <Route path="checklists"         element={<ProtectedRoute permission="hq.admin.checklists"><ChecklistsAdmin /></ProtectedRoute>} />
            <Route path="suggestions"        element={<ProtectedRoute permission="hq.suggestions.view"><SuggestionsAdmin /></ProtectedRoute>} />
            <Route path="dev-requests"       element={<ProtectedRoute permission="hq.dev_requests.view"><DevRequestsAdmin /></ProtectedRoute>} />
            <Route path="offboarding"        element={<ProtectedRoute permission="hq.admin.offboarding"><OffboardingAdmin /></ProtectedRoute>} />
            <Route path="discipline"         element={<ProtectedRoute permission="hq.admin.discipline"><DisciplineAdmin /></ProtectedRoute>} />
            <Route path="safety"             element={<ProtectedRoute permission="hq.admin.safety"><SafetyAdmin /></ProtectedRoute>} />
            <Route path="background-checks"  element={<ProtectedRoute permission="hq.admin.background_checks"><BackgroundChecksAdmin /></ProtectedRoute>} />
            <Route path="sops"               element={<ProtectedRoute permission="hq.sops.manage"><SOPsAdmin /></ProtectedRoute>} />
            <Route path="documents"          element={<ProtectedRoute permission="hq.admin.resources"><ResourcesAdmin /></ProtectedRoute>} />
            <Route path="resources" element={<ProtectedRoute permission="hq.admin.resources"><Navigate to="/admin/documents" replace /></ProtectedRoute>} />
            <Route path="drive"              element={<ProtectedRoute permission="hq.admin.drive"><DriveAdmin /></ProtectedRoute>} />
            <Route path="chat"               element={<ProtectedRoute permission="hq.admin.chat"><ChatAdmin /></ProtectedRoute>} />
            <Route path="audit"              element={<ProtectedRoute permission="hq.admin.audit"><AuditLog /></ProtectedRoute>} />
            <Route path="roadmap"            element={<ProtectedRoute permission="hq.admin.roadmap"><Roadmap /></ProtectedRoute>} />
            <Route path="product-dev"                  element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevHome /></ProtectedRoute>} />
            <Route path="product-dev/compare"          element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevCompare /></ProtectedRoute>} />
            <Route path="product-dev/roadmap"          element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevRoadmap /></ProtectedRoute>} />
            <Route path="product-dev/catalog"          element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevCatalog /></ProtectedRoute>} />
            <Route path="product-dev/pricing-matrix"   element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevPricingMatrix /></ProtectedRoute>} />
            <Route path="product-dev/analytics"        element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevAnalytics /></ProtectedRoute>} />
            <Route path="product-dev/vendors"          element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevVendorsHome /></ProtectedRoute>} />
            <Route path="product-dev/vendors/:id"      element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevVendorDetail /></ProtectedRoute>} />
            <Route path="product-dev/:id"              element={<ProtectedRoute permission="hq.admin.product_dev"><ProductDevDetail /></ProtectedRoute>} />
            <Route path="bugs"               element={<ProtectedRoute permission="hq.bugs.view"><BugsAdmin /></ProtectedRoute>} />
          </Route>

          <Route path="/accountant" element={<ProtectedRoute allow={["accountant"]}><Outlet /></ProtectedRoute>}>
            <Route path="employees" element={<AcctRoster />} />
            <Route path="time" element={<AcctTime />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </AuthProvider>
  );
}

